import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MsalService, MsalModule, MsalBroadcastService, 
  MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest,
  EventMessage, EventType, AccountInfo } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { UserModel } from './models/userModel';
import { MfgappsApiService } from './services/mfgapps-api.service';
import { MfgappsEventService } from './services/mfgapps-event.service';
import { TmnaMqcsUiService } from 'tmna-mqcs-ui/src/lib/services/init';
import { SidebarComponent } from './layout/sidebar/sidebar.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MsalModule, ConfirmDialog, ToastModule, SidebarComponent],
  templateUrl: './app.component.html',
  styleUrls: ["./app.component.scss"]
})
export class AppComponent implements OnInit, OnDestroy  {
  title = 'Sayka !!! Share Achievements, Your Kaizen & Actions !!!';
  authMessage = '';
  activeAccount!: AccountInfo;
  user!: UserModel;
  isIframe = false;
  loginDisplay = false;
  sidebarCollapsed = true;
  activeShellNav = 'success';
  shellHeaderTitle = 'All Success Entries';
  private readonly _destroying$ = new Subject<void>();
  envColor: string = '';
  envLabel: string = '';
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly apiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService,
    private readonly tmnaMqcsUiService: TmnaMqcsUiService,
    private readonly router: Router
  ) {
    this.tmnaMqcsUiService.tmnaMqcsUiInit();
  }

  ngOnInit() {
    this.authMessage = 'Authentication In Progress';
    const environment = this.detectEnvironment();
    this.eventService.isDevelopmentEnvironment = environment === 'LOCAL' || environment === 'DEV';
    this.setEnvironmentIndicator(environment);
    if (this.authService.instance.getAllAccounts().length > 0) {
      (async() => await this.checkAndSetActiveAccount())();
    }
    this.authService.handleRedirectObservable().pipe(
      takeUntil(this._destroying$)
    ).subscribe();
    
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((_result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$.pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      ).subscribe(async() => {
        this.setLoginDisplay();
        await this.checkAndSetActiveAccount();
      });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this._destroying$)
    ).subscribe(event => {
      this.setActiveShellNav(event.urlAfterRedirects);
    });

    this.setActiveShellNav(this.router.url);
  }

  private detectEnvironment(): 'LOCAL' | 'QA/STAGING' | 'DEV' | 'PROD' {
    const currentUrl = window.location.href;

    if (currentUrl.includes('localhost') || currentUrl.includes('127.0.0.1')) {
      return 'LOCAL';
    }
    if (currentUrl.includes('staging.') || currentUrl.includes('qa.')) {
      return 'QA/STAGING';
    }
    if (currentUrl.includes('dev.')) {
      return 'DEV';
    }
    return 'PROD';
  }

  private setEnvironmentIndicator(environment: string): void {
    if (environment === 'LOCAL') {
      this.envColor = '#4CAF50'; // Vibrant Green for Local Development
      this.envLabel = 'LOCAL';
    } else if (environment === 'QA/STAGING') {
      this.envColor = '#FFC107'; // Deep Amber/Yellow for Staging/QA Testing
      this.envLabel = 'QA/STAGING';
    } else if (environment === 'DEV') {
      this.envColor = '#2196F3'; // Bright Blue for Shared Dev Servers (Optional)
      this.envLabel = 'DEV';
    } else {
      // Production defaults: Keeps overlays hidden on the live website
      this.envColor = '';
      this.envLabel = '';
    }
  }
  
  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  async checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    this.activeAccount = this.authService.instance.getActiveAccount();
    if (!this.activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      this.activeAccount = accounts[0];
    }
    if (this.activeAccount) {
      //call set user model only if user id has changed, not needed when re-authenticate through popup
      if (this.user?.emailAddress !== this.activeAccount?.username) {
        await this.setUserModel();
      }
    }
    else {
      this.authMessage = 'You are not Authorized to view this application !!';
    }
  }

  async setUserModel() {
    this.user = new UserModel();
    if (this.activeAccount?.idTokenClaims) {
      //Take user account name and workdayID from activeAccount object
      this.user.name = this.activeAccount.name ?? '';
      //Remove suffix e.g. (TMNA) inside parentheses from display name
      const bracketIndex = this.user.name.lastIndexOf('(');
      this.user.displayName = bracketIndex < 0 ? this.user.name : this.user.name.slice(0, bracketIndex).trim();
      this.user.emailAddress = this.activeAccount.username;
      this.user.workdayID = this.activeAccount.idTokenClaims["WorkDayID"] as string;
      this.user.idClaimCompany = this.activeAccount.idTokenClaims["Company"] as string;
      this.user.officeLocation = this.activeAccount.idTokenClaims["OfficeLocation"] as string;
      const roles = this.activeAccount.idTokenClaims["roles"];
      this.user.roleName = this.user.roleId = roles.some(r => r.includes('admin')) ? "Admin" : "User";
      this.user.isSuperAdmin = this.user.roleName === "Admin";
      //We get user roles from Aurora DB with separate API call if user is not super admin in Azure Entra
      if (!this.user.isSuperAdmin) {
        await this.getUserRoles();
      }
      Object.freeze(this.user);
      this.eventService.setActiveAccount(this.user);
      this.authMessage = '';
    }
  }

  async getUserRoles() {
    this.authMessage = 'Authorization In Progress';
    const response = await this.apiService.getUserRoles(this.user.workdayID);
    if (response.result) {
      this.user.accessList = response.result.access_list ?? [];
      this.user.roleId = response.result.role_id ?? "User";
      this.user.roleName = response.result.role_name ?? "User";
     /* if (this.user.roleName === 'Company Admin') {
        //Set first company_id as user's adminCompanyId since user can be admin of only one company
        this.user.adminCompanyId = this.user.accessList[0]?.company_id;
      }*/
    }
  }

  loginRedirect() {
    let authRequest: RedirectRequest;
    if (this.msalGuardConfig.authRequest) {
      authRequest = { ...this.msalGuardConfig.authRequest } as RedirectRequest;
    }
    this.authService.loginRedirect(authRequest);
  }

  loginPopup() {
    let authRequest: PopupRequest;
    if (this.msalGuardConfig.authRequest) {
      authRequest = { ...this.msalGuardConfig.authRequest } as PopupRequest;
    }
    this.authService.loginPopup(authRequest).pipe(
      takeUntil(this._destroying$)
    ).subscribe((response: AuthenticationResult) => {
      this.authService.instance.setActiveAccount(response.account);
    });
  }

  closeSidebar() {
    this.sidebarCollapsed = false;
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  private setActiveShellNav(url: string) {
    const normalizedUrl = (url || '').toLowerCase();
    if (normalizedUrl.startsWith('/dashboard')) {
      this.activeShellNav = 'dashboard';
      this.shellHeaderTitle = 'Dashboard';
    }
    else if (normalizedUrl.startsWith('/support-hub')) {
      this.activeShellNav = 'support-hub';
      this.shellHeaderTitle = 'Support Hub';
    }
    else if (normalizedUrl.startsWith('/analytics') || normalizedUrl.startsWith('/reporting')) {
      this.activeShellNav = 'analytics';
      if (normalizedUrl.startsWith('/analytics/deployment') || normalizedUrl.startsWith('/reporting/deployment')) {
        this.shellHeaderTitle = '';
      }
      else if (
        normalizedUrl.startsWith('/analytics/snapshot') ||
        normalizedUrl.startsWith('/reporting/snapshot') ||
        normalizedUrl === '/analytics' ||
        normalizedUrl === '/reporting'
      ) {
        this.shellHeaderTitle = 'Snapshot';
      }
      else if (normalizedUrl.startsWith('/analytics/utilization') || normalizedUrl.startsWith('/reporting/utilization')) {
        this.shellHeaderTitle = 'Utilization';
      }
      else {
        this.shellHeaderTitle = 'Analytics';
      }
    }
    else if (normalizedUrl.startsWith('/dt-calculator')) {
      this.activeShellNav = 'dt-calculator';
      this.shellHeaderTitle = 'Downtime Cost Calculator';
    }
    else if (normalizedUrl.startsWith('/shop-details')) {
      this.activeShellNav = 'shop-details';
      this.shellHeaderTitle = 'Shop Details';
    }
    else if (normalizedUrl.startsWith('/settings')) {
      this.activeShellNav = 'settings';
      this.shellHeaderTitle = 'Settings';
    }
    else if (normalizedUrl.startsWith('/success')) {
      this.activeShellNav = 'success';
      // Determine the specific header for success-related routes
      if (normalizedUrl.includes('/entry/new')) {
        this.shellHeaderTitle = 'New Success Entry';
      }
      else if (normalizedUrl.includes('/entry/') && normalizedUrl.includes('/edit')) {
        this.shellHeaderTitle = 'Edit Success Entry';
      }
      else if (normalizedUrl.includes('/entry/') && normalizedUrl.includes('/view')) {
        this.shellHeaderTitle = 'View Success Entry';
      }
      else {
        this.shellHeaderTitle = 'All Success Entries';
      }
    }
    else {
      this.activeShellNav = 'success';
      this.shellHeaderTitle = 'All Success Entries';
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}